import { Fragment, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { tenantApi, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DangerZoneDeleteTenant from "@/components/DangerZoneDeleteTenant";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search } from "lucide-react";

/** Platform-operator dashboard — calls GET /api/v1/platform/metrics and
 * /api/v1/platform/tenants (contracts doc section 2). Supersedes Hub's
 * legacy GET /platform/tenants per decision 001. */
export default function PlatformDashboard() {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole("P-SA"); // only P-SA may hard-delete a tenant
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null); // tenant whose danger zone is open

  useEffect(() => {
    Promise.all([tenantApi.get("/platform/metrics"), tenantApi.get("/platform/tenants")])
      .then(([m, t]) => {
        setMetrics(m.data);
        setTenants(t.data);
      })
      .catch((err) => { const m = errorMessage(err); if (m) setError(m); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = tenants.filter((row) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (row.company_name || "").toLowerCase().includes(q) || row.plan_code.toLowerCase().includes(q);
  });

  return (
    <Layout variant="platform">
      <h1 className="font-heading text-2xl font-bold">Platform Dashboard</h1>
      <p className="mt-1 text-muted-foreground">All tenants, billing and revenue.</p>

      {loading && <Loader2 className="mt-8 h-6 w-6 animate-spin text-primary" />}
      {error && <p className="mt-8 text-sm text-destructive" data-testid="platform-dashboard-error">{error}</p>}

      {metrics && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader><CardDescription>Active</CardDescription><CardTitle data-testid="metric-active">{metrics.active_count}</CardTitle></CardHeader>
          </Card>
          <Card>
            <CardHeader><CardDescription>Trialing</CardDescription><CardTitle>{metrics.trialing_count}</CardTitle></CardHeader>
          </Card>
          <Card>
            <CardHeader><CardDescription>MRR</CardDescription><CardTitle data-testid="metric-mrr">${(metrics.mrr_cents / 100).toFixed(0)}</CardTitle></CardHeader>
          </Card>
          <Card>
            <CardHeader><CardDescription>ARR</CardDescription><CardTitle>${(metrics.arr_cents / 100).toFixed(0)}</CardTitle></CardHeader>
          </Card>
          <Card>
            <CardHeader><CardDescription>Signups (30d)</CardDescription><CardTitle>{metrics.signups_last_30_days}</CardTitle></CardHeader>
          </Card>
        </div>
      )}

      <Card className="mt-6">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Tenants</CardTitle>
          <div className="flex items-center gap-2 rounded-md border border-input px-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              className="h-8 w-56 border-0 focus-visible:ring-0"
              placeholder="Search tenants or plans..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="platform-tenant-search"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">{tenants.length === 0 ? "No tenants yet." : "No tenants match your search."}</p>
          ) : (
            <table className="w-full text-sm" data-testid="platform-tenants-table">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2">Company</th>
                  <th className="py-2">Plan</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">MRR contribution</th>
                  <th className="py-2">Signed up</th>
                  {isSuperAdmin && <th className="py-2 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <Fragment key={row.tenant_id}>
                    <tr className="border-b last:border-0">
                      <td className="py-2 font-medium">{row.company_name || <span className="text-muted-foreground">—</span>}</td>
                      <td className="py-2 capitalize">{row.plan_code}</td>
                      <td className="py-2">
                        <Badge variant={row.status === "active" ? "success" : "secondary"}>{row.status}</Badge>
                      </td>
                      <td className="py-2">${(row.mrr_contribution_cents / 100).toFixed(0)}</td>
                      <td className="py-2">{new Date(row.created_at).toLocaleDateString()}</td>
                      {isSuperAdmin && (
                        <td className="py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedId((id) => (id === row.tenant_id ? null : row.tenant_id))}
                            data-testid={`platform-tenant-manage-${row.tenant_id}`}
                          >
                            {expandedId === row.tenant_id ? "Close" : "Manage"}
                          </Button>
                        </td>
                      )}
                    </tr>
                    {isSuperAdmin && expandedId === row.tenant_id && (
                      <tr className="border-b last:border-0">
                        <td colSpan={6} className="py-3">
                          <DangerZoneDeleteTenant
                            tenant={row}
                            onDeleted={(deletedId) => {
                              setTenants((prev) => prev.filter((t) => t.tenant_id !== deletedId));
                              setExpandedId(null);
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
